// useStableCallback.js
// A stable function reference that always invokes the LATEST closure.
// Behaves like useCallback(fn, []) — same identity for the component's lifetime —
// but without any of the stale-closure footguns. No deps array required.
//
// This is the "useEffectEvent" / "useEvent" pattern (RFC 0220).
// Use it for event handlers passed to memoized children: the reference is stable
// so React.memo can skip re-renders, but inside the handler you always see the
// freshest props/state — no dependency tracking to forget.
//
// Example:
//   const onClick = useStableCallback(() => {
//     // reads the latest count, no deps needed
//     setCount(count + 1);
//   });
//   return <MemoChild onClick={onClick} />;  // onClick never changes identity
//
// IMPORTANT: don't read the callback during render of the same component —
// it's intended to be invoked from effects, events, or child renders.

import { useCallback, useLayoutEffect, useRef } from "react";

export function useStableCallback(callback) {
  const ref = useRef(callback);

  // Keep the ref pointed at the most recent function on every render.
  // useLayoutEffect (not useEffect) so refs are updated before any children
  // that might invoke this in their own layout effects.
  useLayoutEffect(() => {
    ref.current = callback;
  });

  // The returned function is stable across renders — its identity never changes,
  // so it's safe to put inside React.memo'd children's props.
  return useCallback((...args) => ref.current(...args), []);
}

export default useStableCallback;
